/**
 * FhirVitalsChartWidget — Vitals trending chart for subject dashboard (DRR-321)
 *
 * Shows vital sign observations as a time-series chart.
 * Uses the existing ObservationChart component for rendering.
 */

import React, { useMemo, useState } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Skeleton,
  Alert,
  SegmentedControl,
  Paper,
} from '@mantine/core';
import { IconHeartRateMonitor, IconAlertCircle, IconChartLine } from '@tabler/icons-react';
import { useAdminListFhirObservationsQuery } from '@ferp/core/generated/graphql';
import { ObservationChart, type ObservationDataPoint } from '../components/ObservationChart';
import type { WidgetProps } from '@ferp/core';

interface ParsedObservation {
  id: string;
  code: string;
  display: string;
  value?: number;
  unit?: string;
  status: string;
  effectiveDate?: Date;
  loincCode?: string;
}

const VITAL_LOINC_CODES: Record<string, string> = {
  '8480-6': 'Systolic BP',
  '8462-4': 'Diastolic BP',
  '8867-4': 'Heart Rate',
  '8310-5': 'Temperature',
  '9279-1': 'Respiratory Rate',
  '2708-6': 'SpO2',
  '29463-7': 'Weight',
  '8302-2': 'Height',
  '39156-5': 'BMI',
};

function parseObservation(resourceId: string, json: string): ParsedObservation | null {
  try {
    const f = JSON.parse(json) as Record<string, unknown>;

    const codeCC = f.code as { text?: string; coding?: Array<{ code?: string; display?: string }> } | undefined;
    const coding = codeCC?.coding?.[0];
    const code = coding?.code ?? '';
    const display = codeCC?.text ?? coding?.display ?? VITAL_LOINC_CODES[code] ?? 'Observation';

    const valueQty = f.valueQuantity as { value?: number; unit?: string } | undefined;

    let effectiveDate: Date | undefined;
    if (typeof f.effectiveDateTime === 'string') {
      effectiveDate = new Date(f.effectiveDateTime);
    } else if (f.effectivePeriod && typeof (f.effectivePeriod as Record<string, unknown>).start === 'string') {
      effectiveDate = new Date((f.effectivePeriod as Record<string, unknown>).start as string);
    }

    return {
      id: resourceId,
      code,
      display,
      value: valueQty?.value,
      unit: valueQty?.unit,
      status: (f.status as string) ?? 'unknown',
      effectiveDate,
      loincCode: code,
    };
  } catch {
    return null;
  }
}

type VitalType = 'bp' | 'hr' | 'temp' | 'spo2' | 'weight';

const VITAL_CONFIG: Record<VitalType, { label: string; codes: string[]; unit: string; color: string }> = {
  bp: { label: 'Blood Pressure', codes: ['8480-6', '8462-4'], unit: 'mmHg', color: 'red' },
  hr: { label: 'Heart Rate', codes: ['8867-4'], unit: 'bpm', color: 'pink' },
  temp: { label: 'Temperature', codes: ['8310-5'], unit: '°F', color: 'orange' },
  spo2: { label: 'SpO2', codes: ['2708-6'], unit: '%', color: 'cyan' },
  weight: { label: 'Weight', codes: ['29463-7'], unit: 'kg', color: 'blue' },
};

export function FhirVitalsChartWidget({ tenantNum }: WidgetProps) {
  const [selectedVital, setSelectedVital] = useState<VitalType>('bp');

  const { data, loading, error } = useAdminListFhirObservationsQuery({
    variables: { tenantNum: tenantNum ?? 0, limit: 100 },
    skip: !tenantNum,
    fetchPolicy: 'cache-and-network',
  });

  const observations = useMemo(() => {
    const raw = data?.adminListFhirObservations ?? [];
    return raw
      .map(r => parseObservation(r.resourceId, r.fhirJson))
      .filter((o): o is ParsedObservation => o !== null && o.value !== undefined);
  }, [data]);

  const vitalCounts = useMemo(() => {
    const counts: Record<VitalType, number> = { bp: 0, hr: 0, temp: 0, spo2: 0, weight: 0 };
    for (const obs of observations) {
      for (const [vital, config] of Object.entries(VITAL_CONFIG)) {
        if (config.codes.includes(obs.loincCode ?? '')) {
          counts[vital as VitalType]++;
        }
      }
    }
    return counts;
  }, [observations]);

  const chartData = useMemo((): ObservationDataPoint[] => {
    const config = VITAL_CONFIG[selectedVital];
    return observations
      .filter(obs => config.codes.includes(obs.loincCode ?? ''))
      .filter(obs => obs.effectiveDate)
      .sort((a, b) => (a.effectiveDate?.getTime() ?? 0) - (b.effectiveDate?.getTime() ?? 0))
      .map(obs => ({
        date: obs.effectiveDate!.toISOString(),
        value: obs.value!,
        unit: obs.unit ?? config.unit,
      }));
  }, [observations, selectedVital]);

  if (loading && observations.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={40} />
        <Skeleton height={150} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading vitals.
      </Alert>
    );
  }

  if (observations.length === 0) {
    return (
      <Alert icon={<IconHeartRateMonitor size={16} />} color="blue" variant="light">
        No vital signs recorded for this subject.
      </Alert>
    );
  }

  const config = VITAL_CONFIG[selectedVital];

  return (
    <Stack gap="sm">
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <IconChartLine size={16} color="gray" />
          <Text fw={600} size="sm">Vitals Trend</Text>
        </Group>
        <Badge size="sm" color="blue">{observations.length} readings</Badge>
      </Group>

      <SegmentedControl
        size="xs"
        value={selectedVital}
        onChange={(v) => setSelectedVital(v as VitalType)}
        data={Object.entries(VITAL_CONFIG)
          .filter(([key]) => vitalCounts[key as VitalType] > 0)
          .map(([key, cfg]) => ({
            value: key,
            label: `${cfg.label} (${vitalCounts[key as VitalType]})`,
          }))}
      />

      {chartData.length > 0 ? (
        <Paper p="sm" withBorder>
          <ObservationChart
            data={chartData}
            observationType={config.label}
            color={config.color}
          />
        </Paper>
      ) : (
        <Alert icon={<IconHeartRateMonitor size={16} />} color="gray" variant="light">
          No {config.label.toLowerCase()} readings recorded.
        </Alert>
      )}
    </Stack>
  );
}
